import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAttendance,
  fetchSubjects,
  fetchPapers,
} from "../../redux/actions/attendanceActions";
import {
  FiSearch,
  FiCalendar,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiDownload,
  FiX,
  FiUser,
  FiBook,
  FiBookOpen,
  FiChevronDown,
  FiClock,
  FiCheckCircle,
  FiWatch,
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const Attendance = () => {
  const dispatch = useDispatch();
  const { attendance, pagination, loading, error, subjects, papers } =
    useSelector((state) => state.attendance);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [subjectId, setSubjectId] = useState("");
  const [paperId, setPaperId] = useState("");
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [showPaperDropdown, setShowPaperDropdown] = useState(false);
  const [subjectSearch, setSubjectSearch] = useState("");
  const [paperSearch, setPaperSearch] = useState("");
  const subjectSelectRef = useRef(null);
  const paperSelectRef = useRef(null);
  const subjectDropdownRef = useRef(null);
  const paperDropdownRef = useRef(null);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    sem: "",
    is_hons: "",
    session: "",
    period: "",
    class_type: "",
  });

  const isInitialLoad = useRef(true);

  // Filter subjects based on search
  const filteredSubjects =
    subjects?.filter((subject) =>
      subject.subject_name.toLowerCase().includes(subjectSearch.toLowerCase()),
    ) || [];

  // Filter papers based on search (includes paper_name and paper_type)
  const filteredPapers =
    papers?.filter(
      (paper) =>
        paper.paper_name.toLowerCase().includes(paperSearch.toLowerCase()) ||
        paper.paper_type?.toLowerCase().includes(paperSearch.toLowerCase()),
    ) || [];

  // Fetch subjects on mount
  useEffect(() => {
    dispatch(fetchSubjects());
  }, [dispatch]);

  // Fetch papers when subject changes (EXACTLY like PaperWiseReports)
  useEffect(() => {
    if (subjectId) {
      dispatch(fetchPapers(subjectId));
      // Clear paper selection when subject changes
      setSelectedPaper(null);
      setPaperId("");
    } else {
      // Fetch all papers if no subject selected
      dispatch(fetchPapers());
    }
  }, [dispatch, subjectId]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        subjectDropdownRef.current &&
        !subjectDropdownRef.current.contains(event.target) &&
        subjectSelectRef.current &&
        !subjectSelectRef.current.contains(event.target)
      ) {
        setShowSubjectDropdown(false);
      }
      if (
        paperDropdownRef.current &&
        !paperDropdownRef.current.contains(event.target) &&
        paperSelectRef.current &&
        !paperSelectRef.current.contains(event.target)
      ) {
        setShowPaperDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Main useEffect to fetch attendance with filters
  useEffect(() => {
    const timer = setTimeout(
      () => {
        dispatch(
          fetchAttendance({
            page: currentPage,
            limit: limit,
            search: searchTerm,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            sem: filters.sem,
            is_hons: filters.is_hons,
            period: filters.period,
            class_type: filters.class_type,
            paper_id: paperId, // Only paper_id matters, subject_id is not used by API
          }),
        );
      },
      isInitialLoad.current ? 0 : 500,
    );

    isInitialLoad.current = false;
    return () => clearTimeout(timer);
  }, [searchTerm, filters, currentPage, limit, paperId, dispatch]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(
      fetchAttendance({
        page: currentPage,
        limit: limit,
        search: searchTerm,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        sem: filters.sem,
        is_hons: filters.is_hons,
        period: filters.period,
        class_type: filters.class_type,
        paper_id: paperId,
      }),
    );
    toast.success("Attendance list refreshed");
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setFilters({
      fromDate: "",
      toDate: "",
      sem: "",
      is_hons: "",
      session: "",
      period: "",
      class_type: "",
    });
    setCurrentPage(1);
    toast.success("Search cleared");
  };

  // Apply filters
  const handleApplyFilters = () => {
    setCurrentPage(1);
    setShowFilters(false);
    toast.success("Filters applied");
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      sem: "",
      is_hons: "",
      session: "",
      period: "",
      class_type: "",
    });
    setCurrentPage(1);
    toast.success("Filters cleared");
  };

  // Get course type
  const getCourseType = (is_hons) => {
    return is_hons === 1 ? "Honors" : "Regular";
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      return date
        .toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace(",", " at");
    } catch (error) {
      return "Date Error";
    }
  };

  // Get period label
  const getPeriodLabel = (period) => {
    switch (period) {
      case 1:
        return "1st Period";
      case 2:
        return "2nd Period";
      case 3:
        return "3rd Period";
      case 4:
        return "4th Period";
      case 5:
        return "5th Period";
      case 6:
        return "6th Period";
      case 7:
        return "7th Period";
      case 8:
        return "8th Period";
      default:
        return `Period ${period}`;
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (attendance.length === 0) {
      toast.error("No data to export");
      return;
    }
    try {
      const exportData = attendance.map((record) => ({
        "Student Name": record.student_name || "",
        "Paper Name": record.paper_name || "",
        "Teacher Name": record.teacher_name || "",
        Session: record.session_name || "",
        Semester: record.sem || "",
        "Course Type": getCourseType(record.is_hons),
        "Attendance Date": formatDate(record.attendance_date),
        Period: getPeriodLabel(record.period),
        Status: record.is_present === 1 ? "Present" : "Absent",
        "Class Type": record.class_type || "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `GMGC_Attendance_${timestamp}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${attendance.length} records`);
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  // Get unique counts for stats
  const getUniqueStats = () => {
    const uniqueStudents = [...new Set(attendance.map((a) => a.student_name))]
      .length;
    const uniqueSubjects = [...new Set(attendance.map((a) => a.paper_name))]
      .length;
    const honorsStudents = attendance.filter((a) => a.is_hons === 1).length;
    const regularStudents = attendance.filter((a) => a.is_hons === 0).length;
    return { uniqueStudents, uniqueSubjects, honorsStudents, regularStudents };
  };

  const stats = getUniqueStats();

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setSubjectId(subject.id);
    setSelectedPaper(null);
    setPaperId("");
    setShowSubjectDropdown(false);
    setSubjectSearch("");
  };

  const handlePaperSelect = (paper) => {
    setSelectedPaper(paper);
    setPaperId(paper.id);
    setShowPaperDropdown(false);
    setPaperSearch("");
  };

  const handleClearSubject = () => {
    setSelectedSubject(null);
    setSubjectId("");
    setSelectedPaper(null);
    setPaperId("");
  };

  const handleClearPaper = () => {
    setSelectedPaper(null);
    setPaperId("");
  };

  const getPaperDisplayName = (paper) => {
    return paper.paper_type
      ? `${paper.paper_name} (${paper.paper_type})`
      : paper.paper_name;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCalendar className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading Attendance
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-xl transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Attendance Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage student attendance records at Gokhale Memorial
            Girls' College
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          <button
            onClick={handleExportExcel}
            disabled={attendance.length === 0}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
              attendance.length === 0
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white"
            }`}
          >
            <FiDownload /> Export Excel
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2.5 rounded-xl"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 mb-8">
        {/* Search input */}
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by student name, paper, or teacher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none transition text-gray-900 dark:text-white"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters button */}
        <div className="flex justify-end mt-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
              showFilters
                ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
            }`}
          >
            <FiFilter /> Filters
          </button>
        </div>

        {/* Filter Options Popup */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Subject Select - Same as PaperWiseReports */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject
                    </label>
                    <div className="relative" ref={subjectSelectRef}>
                      <button
                        onClick={() =>
                          setShowSubjectDropdown(!showSubjectDropdown)
                        }
                        className="w-full flex items-center justify-between px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      >
                        <span className="truncate">
                          {selectedSubject
                            ? selectedSubject.subject_name
                            : "Select Subject"}
                        </span>
                        <FiChevronDown
                          className={`w-4 h-4 transition-transform ${showSubjectDropdown ? "rotate-180" : ""}`}
                        />
                      </button>
                      {showSubjectDropdown && (
                        <div
                          ref={subjectDropdownRef}
                          className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto"
                        >
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="Search subject..."
                              className="w-full px-3 py-1 border rounded text-sm"
                              value={subjectSearch}
                              onChange={(e) => setSubjectSearch(e.target.value)}
                              autoFocus
                            />
                          </div>
                          {filteredSubjects.length > 0 ? (
                            filteredSubjects.map((subject) => (
                              <button
                                key={subject.id}
                                onClick={() => handleSubjectSelect(subject)}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm ${
                                  subjectId === subject.id
                                    ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700"
                                    : ""
                                }`}
                              >
                                {subject.subject_name}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              No subjects found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Paper Select - Same as PaperWiseReports (ALWAYS ENABLED) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Paper
                    </label>
                    <div className="relative" ref={paperSelectRef}>
                      <button
                        onClick={() => setShowPaperDropdown(!showPaperDropdown)}
                        className="w-full flex items-center justify-between px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      >
                        <span className="truncate">
                          {selectedPaper
                            ? getPaperDisplayName(selectedPaper)
                            : "Select Paper"}
                        </span>
                        <div className="flex items-center gap-2">
                          {paperId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearPaper();
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          )}
                          <FiChevronDown
                            className={`w-4 h-4 transition-transform ${showPaperDropdown ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>
                      {showPaperDropdown && (
                        <div
                          ref={paperDropdownRef}
                          className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto"
                        >
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="Search paper..."
                              className="w-full px-3 py-1 border rounded text-sm"
                              value={paperSearch}
                              onChange={(e) => setPaperSearch(e.target.value)}
                              autoFocus
                            />
                          </div>
                          {filteredPapers.length > 0 ? (
                            filteredPapers.map((paper) => (
                              <button
                                key={paper.id}
                                onClick={() => handlePaperSelect(paper)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                              >
                                <div>{paper.paper_name}</div>
                                {paper.paper_type && (
                                  <div className="text-xs text-gray-500">
                                    {paper.paper_type}
                                  </div>
                                )}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-sm text-gray-500">
                              No papers found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* From Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      From Date
                    </label>
                    <input
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) =>
                        setFilters({ ...filters, fromDate: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    />
                  </div>

                  {/* To Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      To Date
                    </label>
                    <input
                      type="date"
                      value={filters.toDate}
                      onChange={(e) =>
                        setFilters({ ...filters, toDate: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    />
                  </div>

                  {/* Semester */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Semester
                    </label>
                    <select
                      value={filters.sem}
                      onChange={(e) =>
                        setFilters({ ...filters, sem: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">All Semesters</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                        <option key={sem} value={sem}>
                          Semester {sem}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Course Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Course Type
                    </label>
                    <select
                      value={filters.is_hons}
                      onChange={(e) =>
                        setFilters({ ...filters, is_hons: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">All Types</option>
                      <option value="1">Honors</option>
                      <option value="0">Regular</option>
                    </select>
                  </div>

                  {/* Period */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Period
                    </label>
                    <select
                      value={filters.period}
                      onChange={(e) =>
                        setFilters({ ...filters, period: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">All Periods</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((period) => (
                        <option key={period} value={period}>
                          {getPeriodLabel(period)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Class Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Class Type
                    </label>
                    <select
                      value={filters.class_type}
                      onChange={(e) =>
                        setFilters({ ...filters, class_type: e.target.value })
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">All Types</option>
                      <option value="Theory">Theory</option>
                      <option value="Practical">Practical</option>
                      <option value="Tutorial">Tutorial</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 transition-colors"
                  >
                    Clear all filters
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowFilters(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {[
          {
            title: "Total Records",
            value: pagination?.total || 0,
            color: "from-purple-600 to-pink-600",
            icon: <FiCalendar />,
          },
          {
            title: "Unique Students",
            value: stats.uniqueStudents,
            color: "from-blue-600 to-cyan-600",
            icon: <FiUser />,
          },
          {
            title: "Subjects",
            value: stats.uniqueSubjects,
            color: "from-emerald-600 to-green-600",
            icon: <FiBook />,
          },
          {
            title: "Honors Students",
            value: stats.honorsStudents,
            color: "from-amber-600 to-orange-600",
            icon: <FiCheckCircle />,
          },
          {
            title: "Regular Students",
            value: stats.regularStudents,
            color: "from-indigo-600 to-violet-600",
            icon: <FiUser />,
          },
        ].map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: idx * 0.1 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 dark:border-gray-700 overflow-hidden group"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5 group-hover:opacity-10 transition-opacity`}
            />
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-1">
                    {stat.title}
                  </p>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </h2>
                </div>
                <div
                  className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} text-white shadow-lg`}
                >
                  {stat.icon}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Attendance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Attendance Records
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Showing {attendance.length} of {pagination?.total || 0}{" "}
                attendance records
                {filters.fromDate &&
                  filters.toDate &&
                  ` (${filters.fromDate} to ${filters.toDate})`}
                {filters.period && ` • ${getPeriodLabel(filters.period)}`}
                {selectedPaper && ` • Paper: ${selectedPaper.paper_name}`}
              </p>
            </div>
            {pagination?.totalPages > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Show
                  </span>
                  <select
                    value={limit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                    <option value="2000">2000</option>
                  </select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    entries
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Loading attendance records...
            </p>
          </div>
        ) : attendance.length === 0 ? (
          <div className="py-20 text-center">
            <FiCalendar className="text-6xl text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Attendance Records Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {searchTerm || Object.values(filters).some((f) => f) || paperId
                ? "No records match your search or filters"
                : "No attendance records available"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Subject & Teacher
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Academic Details
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Class Type
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date & Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {attendance.map((record, index) => (
                    <motion.tr
                      key={record.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors group"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md group-hover:scale-105 transition-transform">
                            {record.student_name?.charAt(0) || "S"}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {record.student_name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              ID: ATT{String(record.id).padStart(4, "0")}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <FiBook className="text-gray-400 w-4 h-4" />
                            <p className="text-gray-900 dark:text-white font-medium">
                              {record.paper_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiUser className="text-gray-400 w-4 h-4" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {record.teacher_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium">
                              Sem {record.sem}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${record.is_hons === 1 ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"}`}
                            >
                              {getCourseType(record.is_hons)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FiWatch className="text-gray-400 w-3 h-3" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {getPeriodLabel(record.period)}
                            </p>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-500">
                            Session: {record.session_name}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            record.class_type === "Theory"
                              ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              : record.class_type === "Practical"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                : record.class_type === "Tutorial"
                                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {record.class_type || "N/A"}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <FiClock className="text-gray-400 w-4 h-4" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {formatDate(record.attendance_date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${record.is_present === 1 ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"}`}
                            >
                              {record.is_present === 1 ? (
                                <>
                                  <FiCheckCircle className="w-3 h-3" /> Present
                                </>
                              ) : (
                                "Absent"
                              )}
                            </span>
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {(currentPage - 1) * limit + 1} to{" "}
                  {Math.min(currentPage * limit, pagination?.total || 0)} of{" "}
                  {pagination?.total || 0} records
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${currentPage === 1 ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed" : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"}`}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from(
                      { length: Math.min(5, pagination?.totalPages || 1) },
                      (_, i) => {
                        let pageNum;
                        if ((pagination?.totalPages || 1) <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (
                          currentPage >=
                          (pagination?.totalPages || 1) - 2
                        )
                          pageNum = (pagination?.totalPages || 1) - 4 + i;
                        else pageNum = currentPage - 2 + i;
                        if (
                          pageNum < 1 ||
                          pageNum > (pagination?.totalPages || 1)
                        )
                          return null;
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 rounded-lg transition ${currentPage === pageNum ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg" : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"}`}
                          >
                            {pageNum}
                          </button>
                        );
                      },
                    )}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === (pagination?.totalPages || 1)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition ${currentPage === (pagination?.totalPages || 1) ? "bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed" : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"}`}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Attendance;
